export type ReturnUrlRule = {
  origin: string;
  paths: readonly string[];
};

export type ReturnUrlValidation =
  { ok: true; url: string; relativePath: string } | { ok: false; reason: string };

function normalizeOrigin(rawOrigin: string): string | null {
  try {
    const parsed = new URL(rawOrigin.trim());
    if (!['http:', 'https:'].includes(parsed.protocol)) return null;
    if (parsed.username || parsed.password) return null;
    if (parsed.pathname !== '/' || parsed.search || parsed.hash) return null;
    return parsed.origin;
  } catch {
    return null;
  }
}

function pathMatches(pathname: string, allowedPath: string): boolean {
  if (!allowedPath.startsWith('/')) return false;
  if (allowedPath.endsWith('/*')) {
    const prefix = allowedPath.slice(0, -2);
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  }
  return pathname === allowedPath;
}

export function validateReturnUrl(
  candidate: string,
  rules: readonly ReturnUrlRule[],
): ReturnUrlValidation {
  const trimmed = candidate.trim();
  if (!trimmed || trimmed.startsWith('//')) {
    return { ok: false, reason: 'Malformed return URL' };
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { ok: false, reason: 'Malformed return URL' };
  }

  if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password) {
    return { ok: false, reason: 'Return URL protocol or credentials are not allowed' };
  }

  const matchingRule = rules.find((rule) => {
    const origin = normalizeOrigin(rule.origin);
    return origin !== null && parsed.origin === origin;
  });
  if (!matchingRule) return { ok: false, reason: 'Return URL origin is not allowed' };
  if (!matchingRule.paths.some((path) => pathMatches(parsed.pathname, path))) {
    return { ok: false, reason: 'Return URL path is not allowed' };
  }

  return {
    ok: true,
    // Preserve provider placeholders such as Stripe's {CHECKOUT_SESSION_ID}.
    url: trimmed,
    relativePath: `${parsed.pathname}${parsed.search}${parsed.hash}`,
  };
}

function parseIpv4(address: string): number[] | null {
  const parts = address.split('.');
  if (parts.length !== 4) return null;
  const octets = parts.map((part) => {
    if (!/^\d{1,3}$/.test(part)) return -1;
    const value = Number(part);
    return value <= 255 ? value : -1;
  });
  return octets.every((value) => value >= 0) ? octets : null;
}

function expandIpv6(address: string): number[] | null {
  const zoneIndex = address.indexOf('%');
  const unzoned = (zoneIndex >= 0 ? address.slice(0, zoneIndex) : address)
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .toLowerCase();
  if (!unzoned.includes(':')) return null;

  let normalized = unzoned;
  const ipv4Tail = normalized.match(/(?:^|:)(\d+\.\d+\.\d+\.\d+)$/)?.[1];
  if (ipv4Tail) {
    const octets = parseIpv4(ipv4Tail);
    if (!octets) return null;
    const tail = `${((octets[0]! << 8) | octets[1]!).toString(16)}:${(
      (octets[2]! << 8) |
      octets[3]!
    ).toString(16)}`;
    normalized = normalized.slice(0, -ipv4Tail.length) + tail;
  }

  if ((normalized.match(/::/g) ?? []).length > 1) return null;
  const [leftRaw, rightRaw] = normalized.split('::');
  const left = leftRaw ? leftRaw.split(':') : [];
  const right = rightRaw ? rightRaw.split(':') : [];
  if ([...left, ...right].some((part) => !/^[0-9a-f]{1,4}$/.test(part))) return null;
  const missing = 8 - left.length - right.length;
  if (missing < 0 || (!normalized.includes('::') && missing !== 0)) return null;
  if (normalized.includes('::') && missing < 1) return null;
  return [...left, ...Array(missing).fill('0'), ...right].map((part) => Number.parseInt(part, 16));
}

function isPublicIpv4(octets: number[]): boolean {
  const [a, b] = octets;
  if (a === 0 || a === 10 || a === 127) return false;
  if (a === 100 && b! >= 64 && b! <= 127) return false;
  if (a === 169 && b === 254) return false;
  if (a === 172 && b! >= 16 && b! <= 31) return false;
  if (a === 192 && (b === 0 || b === 168)) return false;
  if (a === 192 && b === 88 && octets[2] === 99) return false;
  if (a === 192 && b === 0 && octets[2] === 2) return false;
  if (a === 192 && b === 31 && octets[2] === 196) return false;
  if (a === 192 && b === 52 && octets[2] === 193) return false;
  if (a === 192 && b === 175 && octets[2] === 48) return false;
  if (a === 198 && (b === 18 || b === 19 || b === 51)) return false;
  if (a === 203 && b === 0 && octets[2] === 113) return false;
  if (a! >= 224) return false;
  return true;
}

export function isPublicIpAddress(address: string): boolean {
  const ipv4 = parseIpv4(address);
  if (ipv4) return isPublicIpv4(ipv4);

  const groups = expandIpv6(address);
  if (!groups) return false;
  const [first, second] = groups;
  if (
    groups.every((group) => group === 0) ||
    (groups.slice(0, 7).every((group) => group === 0) && groups[7] === 1)
  ) {
    return false;
  }

  // IPv4-mapped IPv6.
  if (groups.slice(0, 5).every((group) => group === 0) && groups[5] === 0xffff) {
    return isPublicIpv4([groups[6]! >>> 8, groups[6]! & 0xff, groups[7]! >>> 8, groups[7]! & 0xff]);
  }

  if ((first! & 0xfe00) === 0xfc00) return false; // unique local fc00::/7
  if ((first! & 0xffc0) === 0xfe80) return false; // link local fe80::/10
  if ((first! & 0xffc0) === 0xfec0) return false; // deprecated site local fec0::/10
  if ((first! & 0xff00) === 0xff00) return false; // multicast ff00::/8
  if (first === 0x0100 && second === 0) return false; // discard-only 100::/64
  if (
    first === 0x0064 &&
    second === 0xff9b &&
    (groups[2] === 0 || groups[2] === 1)
  ) {
    return false; // IPv4 translation ranges
  }
  if (first === 0x2001 && second! <= 0x01ff) return false; // IETF protocol assignments
  if (first === 0x2001 && second === 0x0db8) return false; // documentation
  if (first === 0x2002) return false; // 6to4 can encode private IPv4
  if ((first! & 0xfff0) === 0x3ff0) return false; // documentation 3fff::/20
  if (first === 0x5f00) return false; // segment-routing SIDs
  if ((first! & 0xe000) !== 0x2000) return false; // only allocated global-unicast space
  return true;
}

export function isHttpUrl(rawUrl: string): boolean {
  try {
    const url = new URL(rawUrl.trim());
    return (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      !url.username &&
      !url.password &&
      Boolean(url.hostname)
    );
  } catch {
    return false;
  }
}

const BLOCKED_OUTBOUND_HOSTNAMES = new Set([
  'metadata.google.internal',
  'metadata.goog',
  '0.0.0.0',
]);

function normalizeOutboundHostname(hostname: string): string {
  return hostname.replace(/^\[/, '').replace(/\]$/, '').toLowerCase();
}

function isHostnameIpLiteral(hostname: string): boolean {
  const host = normalizeOutboundHostname(hostname);
  return parseIpv4(host) !== null || expandIpv6(host) !== null;
}

function isLocalDevHostname(hostname: string): boolean {
  const host = normalizeOutboundHostname(hostname);
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]';
}

/**
 * Static hostname guard for outbound HTTP(S) targets (webhooks, link previews, etc.).
 * DNS rebinding must still be checked at request time via pinned outbound transport.
 */
export function assertPublicHttpHostname(
  hostname: string,
  options?: { allowLocalhost?: boolean },
): void {
  const host = normalizeOutboundHostname(hostname);
  if (!host) throw new Error('Hostname is required');
  if (BLOCKED_OUTBOUND_HOSTNAMES.has(host)) {
    throw new Error('Hostname is not allowed');
  }
  if (host.endsWith('.localhost') || host.endsWith('.local') || host.endsWith('.internal')) {
    throw new Error('Hostname is not allowed');
  }
  if (isLocalDevHostname(host)) {
    if (!options?.allowLocalhost) {
      throw new Error('Localhost is not allowed');
    }
    return;
  }
  if (isHostnameIpLiteral(host) && !isPublicIpAddress(host)) {
    throw new Error('URL must use a public IP address');
  }
}
